import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Tabs, Tab, Form as BsForm, Row, Col } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { listarMensagens, salvarMensagem, excluirMensagem } from '../services/cobrancaSmsService';
import { SMS_MAX_CHARS, contarCaracteres } from '../utils/validacao';

const ABAS = [
  { key: 'F', label: 'Normal (F)' },
  { key: 'C', label: 'SetCasa (C)' },
  { key: 'J', label: 'Jurídico (J)' },
];

const schemaMensagem = (tipo) =>
  Yup.object({
    de: tipo !== 'J'
      ? Yup.number().min(0, "'De' deve ser >= 0").required("'De' é obrigatório")
      : Yup.number().notRequired(),
    ate: tipo !== 'J'
      ? Yup.number()
          .min(1, "'Até' deve ser >= 1")
          .required("'Até' é obrigatório")
          .test('maior-que-de', "'De' não pode ser maior que 'Até'", function (ate) {
            return this.parent.de <= ate;
          })
      : Yup.number().notRequired(),
    texto: Yup.string()
      .max(SMS_MAX_CHARS, `Máximo ${SMS_MAX_CHARS} caracteres`)
      .required('Texto é obrigatório'),
  });

const FormMensagem = ({ tipo, mensagemEditando, onSalvar, onCancelar, carregando }) => {
  const isJuridico = tipo === 'J';
  const editando = !!mensagemEditando;
  const initialValues = {
    de: mensagemEditando ? mensagemEditando.de : 0,
    ate: mensagemEditando ? mensagemEditando.ate : 30,
    texto: mensagemEditando ? mensagemEditando.texto : '',
    tipo,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schemaMensagem(tipo)}
      onSubmit={onSalvar}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue }) => (
        <Form>
          <Row>
            {!isJuridico && (
              <>
                <Col md={3}>
                  <BsForm.Group>
                    <BsForm.Label>De (dias)</BsForm.Label>
                    <BsForm.Control
                      type="number"
                      min={0}
                      value={values.de}
                      onChange={(e) => setFieldValue('de', Number(e.target.value))}
                      isInvalid={touched.de && !!errors.de}
                      readOnly={editando}
                      disabled={editando}
                    />
                    <BsForm.Control.Feedback type="invalid">{errors.de}</BsForm.Control.Feedback>
                  </BsForm.Group>
                </Col>
                <Col md={3}>
                  <BsForm.Group>
                    <BsForm.Label>Até (dias)</BsForm.Label>
                    <BsForm.Control
                      type="number"
                      min={1}
                      value={values.ate}
                      onChange={(e) => setFieldValue('ate', Number(e.target.value))}
                      isInvalid={touched.ate && !!errors.ate}
                      readOnly={editando}
                      disabled={editando}
                    />
                    <BsForm.Control.Feedback type="invalid">{errors.ate}</BsForm.Control.Feedback>
                  </BsForm.Group>
                </Col>
              </>
            )}
            <Col md={isJuridico ? 12 : 6}>
              <BsForm.Group>
                <BsForm.Label>
                  Texto{' '}
                  <span
                    className={contarCaracteres(values.texto) < 0 ? 'text-danger' : 'text-muted'}
                    style={{ fontSize: '0.85em' }}
                  >
                    ({contarCaracteres(values.texto)} restantes)
                  </span>
                </BsForm.Label>
                <BsForm.Control
                  as="textarea"
                  rows={3}
                  maxLength={SMS_MAX_CHARS}
                  value={values.texto}
                  onChange={(e) => setFieldValue('texto', e.target.value)}
                  isInvalid={touched.texto && !!errors.texto}
                />
                <BsForm.Control.Feedback type="invalid">{errors.texto}</BsForm.Control.Feedback>
              </BsForm.Group>
            </Col>
          </Row>
          <div className="mt-2 d-flex justify-content-end">
            <Button type="button" variant="secondary" onClick={onCancelar} className="mr-2">
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={carregando}>
              {carregando ? 'Salvando...' : 'Gravar'}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

const AbasMensagens = ({ tipo }) => {
  const [mensagens, setMensagens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [mostrando, setMostrando] = useState(false);
  const [mensagemExcluir, setMensagemExcluir] = useState(null);

  const carregar = async () => {
    setCarregando(true);
    try {
      const data = await listarMensagens(tipo);
      setMensagens(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Erro ao carregar mensagens');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
  }, [tipo]);

  const handleSalvar = async (values) => {
    setCarregando(true);
    try {
      await salvarMensagem({ ...values, tipo });
      toast.success('Mensagem salva com sucesso');
      setMostrando(false);
      setEditando(null);
      await carregar();
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar mensagem');
    } finally {
      setCarregando(false);
    }
  };

  const solicitarExcluir = (m) => setMensagemExcluir(m);
  const cancelarExcluir = () => setMensagemExcluir(null);

  const confirmarExcluir = async () => {
    if (!mensagemExcluir) return;
    const m = mensagemExcluir;
    setCarregando(true);
    try {
      await excluirMensagem({ de: m.de, ate: m.ate, tipo });
      toast.success('Mensagem excluída');
      setMensagemExcluir(null);
      await carregar();
    } catch (err) {
      toast.error(err.message || 'Erro ao excluir mensagem');
    } finally {
      setCarregando(false);
    }
  };

  const handleNova = () => {
    setEditando(null);
    setMostrando(true);
  };

  const handleEditar = (m) => {
    setEditando(m);
    setMostrando(true);
  };

  const handleCancelar = () => {
    setMostrando(false);
    setEditando(null);
  };

  const isJuridico = tipo === 'J';

  return (
    <div>
      {!mostrando && (
        <>
          <div className="mb-2 d-flex justify-content-end">
            <Button variant="outline-primary" onClick={handleNova} disabled={isJuridico && mensagens.length > 0}>
              + Nova Mensagem
            </Button>
          </div>
          {carregando ? (
            <p>Carregando...</p>
          ) : mensagens.length === 0 ? (
            <p className="text-muted">Nenhuma mensagem cadastrada.</p>
          ) : (
            <Table striped bordered size="sm">
              <thead>
                <tr>
                  {!isJuridico && <><th>De</th><th>Até</th></>}
                  <th>Texto</th>
                  <th style={{ width: 90, textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {mensagens.map((m) => (
                  <tr key={`${m.tipo}-${m.de}-${m.ate}`}>
                    {!isJuridico && <><td>{m.de}</td><td>{m.ate}</td></>}
                    <td>
                      <span title={m.texto}>
                        {m.texto && m.texto.length > 80 ? `${m.texto.substring(0, 80)}...` : m.texto}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Button
                        variant="link"
                        className="p-0 mr-3"
                        title="Editar"
                        onClick={() => handleEditar(m)}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="link"
                        className="p-0 text-danger"
                        title="Excluir"
                        onClick={() => solicitarExcluir(m)}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      )}
      {mostrando && (
        <FormMensagem
          tipo={tipo}
          mensagemEditando={editando}
          onSalvar={handleSalvar}
          onCancelar={handleCancelar}
          carregando={carregando}
        />
      )}

      <Modal show={!!mensagemExcluir} onHide={cancelarExcluir} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar exclusão</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mensagemExcluir && (
            <>
              <p>Deseja realmente excluir esta mensagem?</p>
              {!isJuridico && (
                <p className="text-muted mb-1">
                  <strong>Faixa:</strong> {mensagemExcluir.de} a {mensagemExcluir.ate} dias
                </p>
              )}
              <p className="text-muted mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                <strong>Texto:</strong>{' '}
                {mensagemExcluir.texto && mensagemExcluir.texto.length > 120
                  ? `${mensagemExcluir.texto.substring(0, 120)}...`
                  : mensagemExcluir.texto}
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelarExcluir} disabled={carregando}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmarExcluir} disabled={carregando}>
            {carregando ? 'Excluindo...' : 'Excluir'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const ModalGerenciarMensagens = ({ show, onClose }) => (
  <Modal show={show} onHide={onClose} size="xl">
    <Modal.Header closeButton>
      <Modal.Title>Gerenciar Mensagens SMS</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Tabs defaultActiveKey="F" id="tabs-mensagens-sms">
        {ABAS.map((aba) => (
          <Tab key={aba.key} eventKey={aba.key} title={aba.label}>
            <div className="pt-3">
              <AbasMensagens tipo={aba.key} />
            </div>
          </Tab>
        ))}
      </Tabs>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onClose}>
        Fechar
      </Button>
    </Modal.Footer>
  </Modal>
);

export default ModalGerenciarMensagens;
